import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { PAGES, WELCOME_TOUR } from '../help/content';

// Help & guided tours.
//
// Seen state lives in localStorage as { [tourId]: version } so a tour that
// gains materially new steps (its `v` bumps) is offered once more instead of
// staying silent forever. It's per browser, deliberately: there's no user-prefs
// store on the server, and a tour re-running on a new device is a smaller
// annoyance than a schema change to tenants.json.

const SEEN_KEY = 'sd-help-seen';
const AUTO_KEY = 'sd-help-autotour';   // 'off' disables first-visit tours

const readSeen = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(SEEN_KEY) || '{}');
    return raw && typeof raw === 'object' ? raw : {};
  } catch {
    return {};
  }
};

const tourFor = (id) => (id === 'welcome' ? WELCOME_TOUR : PAGES[id]?.tour);

const HelpContext = createContext(null);
export const useHelp = () => useContext(HelpContext);

export function HelpProvider({ children }) {
  const [page, setPage] = useState(null);       // active nav page id
  const [tourId, setTourId] = useState(null);   // running tour ('welcome' | page id)
  const [helpOpen, setHelpOpen] = useState(false);
  const [seen, setSeen] = useState(readSeen);
  const [autoTour, setAutoTourState] = useState(() => localStorage.getItem(AUTO_KEY) !== 'off');
  const timers = useRef([]);

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };
  useEffect(() => clearTimers, []);

  const markSeen = useCallback((id) => {
    const t = tourFor(id);
    if (!t) return;
    setSeen(prev => {
      const next = { ...prev, [id]: t.v };
      try { localStorage.setItem(SEEN_KEY, JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  }, []);

  // Starting a tour marks it seen straight away: someone who bails out on step
  // one has made a choice, and re-offering it on every visit would be nagging.
  const startTour = useCallback((id) => {
    const target = id || page;
    if (!tourFor(target)) return;
    clearTimers();
    setHelpOpen(false);
    setTourId(target);
    markSeen(target);
  }, [page, markSeen]);

  const endTour = useCallback(() => {
    clearTimers();
    setTourId(null);
  }, []);

  const setAutoTour = useCallback((on) => {
    setAutoTourState(on);
    try { localStorage.setItem(AUTO_KEY, on ? 'on' : 'off'); } catch { /* private mode */ }
  }, []);

  // Resetting means "offer them again", not "start one now" — so the auto-run
  // stays parked until the viewer moves to another page.
  const skipAuto = useRef(false);
  useEffect(() => { skipAuto.current = false; }, [page]);

  const resetTours = useCallback(() => {
    skipAuto.current = true;
    setSeen({});
    try { localStorage.removeItem(SEEN_KEY); } catch { /* private mode */ }
  }, []);

  const isSeen = useCallback((id) => {
    const t = tourFor(id);
    return !!t && seen[id] >= t.v;
  }, [seen]);

  // "?" opens help from anywhere — unless the viewer is typing into a field.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '?' || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      setTourId(null);
      setHelpOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // First-visit auto-run. The welcome tour wins on a brand-new browser; after
  // that each page offers its own tour once. Page content arrives async, so
  // wait for the first step's target to exist rather than spotlighting a
  // spinner — and give up gracefully if it never shows (empty period, a panel
  // the viewer's role hides).
  useEffect(() => {
    if (!autoTour || tourId || !page || skipAuto.current) return;
    const id = !isSeen('welcome') ? 'welcome' : page;
    const tour = tourFor(id);
    if (!tour || isSeen(id)) return;

    let waited = 0;
    const tick = () => {
      if (document.querySelector(tour.steps[0].target)) {
        startTour(id);
        return;
      }
      waited += 250;
      if (waited < 6000) timers.current.push(setTimeout(tick, 250));
    };
    timers.current.push(setTimeout(tick, 400));
    return clearTimers;
  }, [page, autoTour, tourId, isSeen, startTour]);

  const value = {
    page, setPage,
    tourId, startTour, endTour,
    helpOpen, openHelp: () => { endTour(); setHelpOpen(true); }, closeHelp: () => setHelpOpen(false),
    autoTour, setAutoTour,
    isSeen, resetTours,
    hasTour: (id) => !!tourFor(id || page),
  };

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}
